import React, { useEffect, useState, useCallback, useRef } from 'react';
import PieChart from '../Fires/PieChart.js';
import '../Fires/Fire.scss';
import axios from 'axios';
import moment from 'moment-timezone';
import calendarIcon from '../../../assets/icons/calendar-icon.png';
import locationIcon from '../../../assets/icons/location-icon.png';
import LiveTime from '../Fires/LiveTime.js';
import warningIcon from '../../../assets/icons/warning-icon.png';
import alertIcon from '../../../assets/icons/bell-icon.png';
import fireContainedUrl from '../../../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../../../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../../../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../../../assets/icons/fire-almost-gray.png';
import Spinner from '../../Spinner.js';

export default function TeamFireCard({ fire, team, teamAlerts, setTeamAlerts, getToken, user, setModal }) {
    const [location, setLocation] = useState(null);
    const [country, setCountry] = useState(null);
    const [flagIcon, setFlagIcon] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const fireRef = useRef(null);

    const fetchLocation = async () => {
        console.log(`🌍 Fetching location for fire: ${fire.name} (${fire.longitude}, ${fire.latitude})`);
        try {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${fire.longitude},${fire.latitude}.json`;
  
          const { data } = await axios.get(url, {
            params: {
              access_token:
                'pk.eyJ1IjoiY2VzYXJtbG5keiIsImEiOiJjbHJ0b2JqZjEwOHpiMmtxa25ubzZ0ZXE3In0.18DfsEFZiecc6H8jHdMOIQ'
            }
          });
  
          const context = data.features?.[0]?.context || [];
          let city = '';
          let state = '';
          let countryCode = '';
  
          context.forEach((c) => {
            if (c.id.includes('place')) {
              city = c.text;
            } else if (c.id.includes('region')) {
              state = c.text;
            } else if (c.id.includes('country')) {
              countryCode = c.short_code?.toLowerCase();
            }
          });
  
          setLocation(city && state ? `${city}, ${state}` : '');
          setCountry(countryCode);
          
          // Load flag icon dynamically if country code is available
          if (countryCode) {
            loadFlagIcon(countryCode);
          }
        } catch (err) {
          console.error('Mapbox geocoding failed:', err);
          setLocation('');
          setCountry(null);
          setFlagIcon(null);
        }
    };

    // Intersection Observer to detect when Fire component is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isVisible) {
                    console.log(`🔥 Fire "${fire.name}" became visible - starting location fetch`);
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the component is visible
                rootMargin: '50px' // Start loading 50px before it becomes visible
            }
        );

        if (fireRef.current) {
            observer.observe(fireRef.current);
        }

        return () => {
            if (fireRef.current) {
                observer.unobserve(fireRef.current);
            }
        };
    }, [isVisible, fire.name]);

    // Only fetch location when component becomes visible
    useEffect(() => {
        if (isVisible) {
            // Reset all location-related state when fire changes
            setLocation(null);
            setCountry(null);
            setFlagIcon(null);
            fetchLocation();
        }
    }, [fire.fire_id, isVisible]); // Reset when fire_id changes or becomes visible

    const loadFlagIcon = async (countryCode) => {
        if (!countryCode) return;
        
        try {
            // Dynamically import the flag icon based on country code
            const flagModule = await import(`../../../assets/icons/flag-icons-main/flags/4x3/${countryCode}.svg`);
            setFlagIcon(flagModule.default);
        } catch (error) {
            console.log(`Flag icon for ${countryCode} not found, using fallback`);
            // If the specific flag doesn't exist, we could set a default flag or null
            setFlagIcon(null);
        }
    }

    // Fire icon selection logic
    const returnFireIcon = useCallback((perContained) => {
        const pc = perContained ?? 0;
      
        if (pc < 41)          return fireCircleUrl;            // 0–40
        if (pc < 91)          return fireHalfContainedUrl;     // 41–90
        if (pc < 100)         return fireAlmostGray;           // 91–99
        /* pc === 100 */      return fireContainedUrl;         // 100
    }, []);

    const timeDifference = (startTimestamp, endTimestamp) => {
        const start = new Date(startTimestamp);
        const end = new Date(endTimestamp);

        const diffMs = Math.abs(end - start); // Difference in milliseconds

        const diffSeconds = Math.floor(diffMs / 1000) % 60;
        const diffMinutes = Math.floor(diffMs / (1000 * 60)) % 60;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const parts = [];
        if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
        if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
        if (diffMinutes > 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
        if (diffSeconds > 0) parts.push(`${diffSeconds} second${diffSeconds > 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : '0 seconds';
    }

    const fireContainmentChange = (oldPercentage, newPercentage) => {
        const difference = newPercentage - oldPercentage;
      
        const changeType = difference > 0 ? "increased" : "decreased";
        const absoluteDifference = Math.abs(difference);
      
        return `${changeType} by ${absoluteDifference}%`;
    }

    const formatDate = (timestamp) => {
        const time = moment.utc(timestamp).local();
        return time.format('YYYY-MM-DD hh:mm:ss A');
    }

    const fireIcon = returnFireIcon(fire.perContained);
    
    return (
        <div className='fire-card' ref={fireRef}>
            <div className='fire-card-header'>
                <div className='fire-card-header-title'>
                    <img src={fireIcon} style={{ width: '1.5rem' }}/>
                    {fire.name ? fire.name : 'Not Available'}
                </div>
                <div className='fire-card-header-location-container'>
                    <div className='fire-card-header-location-container-inner'>
                        <div className='fire-card-header-location-container-inner-text'>
                            {!isVisible ? 'Loading...' : (location ? location : <Spinner />)}
                        </div>
                        {country && flagIcon && (
                            <img 
                                src={flagIcon} 
                                alt={`${country.toUpperCase()} flag`}
                                className='fire-card-header-location-container-flag'
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="fire-card-main">
                <div className="fire-card-main-left">
                    <div className="fire-card-main-left-subheader">
                        <div className="fire-card-main-left-subheader-status">
                            <div className="fire-card-main-left-subheader-status-title">Status</div>
                            <div
                                className="fire-card-main-left-subheader-status-value"
                                style={{
                                    color: fire.active ? '#f44336' : '#37c13e',
                                }}
                            >
                                {fire.active ? 'Active' : 'Contained'}
                            </div>
                        </div>
                        {fire.updatedTimestamp && (
                            <div className="fire-card-main-left-subheader-lastupdated">
                                <div className="fire-card-main-left-subheader-lastupdated-title">Last Updated</div>
                                <LiveTime value={fire.updatedTimestamp}/>
                            </div>
                        )}
                    </div>
                    <div className="fire-card-main-left-date">
                        <img src={calendarIcon} className="fire-card-main-left-date-icon" alt="Calendar" />
                        <div className="fire-card-main-left-date-value">
                            {fire.startTimestamp
                                ? formatDate(fire.startTimestamp)
                                : fire.starttimestamp
                                ? formatDate(fire.starttimestamp)
                                : 'Not Available'
                            }
                        </div>
                    </div>
                    <div className="fire-card-main-left-location">
                        <img src={locationIcon} className="fire-card-main-left-location-icon" alt="Location" />
                        <div className="fire-card-main-left-location-value">
                            {`${fire.latitude.toFixed(5)}, ${fire.longitude.toFixed(5)}`}
                        </div>
                    </div>
                </div>
                <div className="fire-card-main-right">
                    <PieChart 
                        percentage={fire.perContained}
                        size={80}
                        type='containment'
                    />
                    <div className="fire-card-main-right-caption">Containment</div>
                </div>
            </div>
            {((fire.perContained || fire.perContained === 0) && fire.last_containment && (fire.last_containment.percontained || fire.last_containment.percontained === 0) && fire.last_containment.timestamp) && (fire.perContained - fire.last_containment.percontained !== 0) &&
                        <div className='fire-card-main-left-containment-update'>
                            <img src={warningIcon} className='fire-card-main-left-containment-update-icon'/>
                            <div className='fire-card-main-left-containment-update-text'>
                                {`Fire containment has `}
                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}>{fireContainmentChange(fire.last_containment.percontained, fire.perContained)}</span>
                                <span 
                                    style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}
                                >
                                    {` in the span of ${timeDifference(fire.last_containment.timestamp, fire.updatedTimestamp ? fire.updatedTimestamp : fire.startTimestamp)}.`}
                                </span>
                            </div>
                        </div>}
            <div className="fire-card-btns">
                <div
                    className='fire-card-btns-btn-alerts-off'
                    style={{ width: '100%' }}
                    onClick={() => {
                        setModal({
                            type: 'team-alerts',
                            content: {
                                fireId: fire.fire_id,
                                teamId: team.id,
                                alerts: teamAlerts,
                                setAlerts: setTeamAlerts,
                                getToken: getToken,
                                user: user
                            }
                        })
                    }}
                >
                    <img 
                        src={alertIcon} 
                        className='fire-card-btns-btn-alerts-off-icon'
                    />
                    Toggle Alert Options
                </div>
            </div>
        </div>
    )
}
